import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { extractErrorMessage } from "@/api/client";
import {
  useKategorie,
  useVyrobcovia,
  type HromadnaZmena,
} from "@/api/hooks/useCennik";
import type { KategoriaItem, Sekcia } from "@/api/schemas";
import { formatPocet, sklonuj } from "@/lib/format";
import { formatKategoria } from "@/lib/kategorie";
import { overCislo } from "@/components/cennik/CenaBunka";
import {
  TAB_LABELS,
  type HromadnaAkcia,
  type MaterialovyTab,
} from "@/components/cennik/cennikZalozky";

const POLOZIEK = ["položku", "položky", "položiek"] as const;
const NOVA = "__nova__";

type Props = {
  /** Otvorená akcia; `null` = okno zatvorené. */
  akcia: HromadnaAkcia | null;
  /** Koľko položiek sa zmení — ukáže sa na tlačidle. */
  pocet: number;
  tab: MaterialovyTab;
  onClose: () => void;
  /**
   * Odošle zmenu. Chybu (napr. 409 — počet sa medzitým zmenil) okno
   * ukáže a ostane otvorené; zavrieť ho má volajúci po úspechu.
   */
  onPotvrdit: (zmena: Partial<HromadnaZmena>) => Promise<void>;
};

const NADPIS: Record<HromadnaAkcia, string> = {
  kategoria: "Zmeniť kategóriu",
  vyrobca: "Nastaviť výrobcu",
  marze: "Nastaviť marže",
  marze_kategorie: "Marže podľa kategórie",
  vyradit: "Vyradiť z cenníka",
  obnovit: "Obnoviť v cenníku",
};

type MarzovePole = "rezia_pct" | "praca_eur_per_mj" | "nadmiera_pct";

/**
 * Polia okna Nastaviť marže — desatinné miesta a strop ako server
 * (`HromadnaZmena`; práca pod strop stĺpca Numeric 10,4, zaokrúhlený,
 * aby hláška nebola „Najviac 999999.9999").
 */
const MARZOVE_POLIA: {
  pole: MarzovePole;
  label: string;
  desatinne: number;
  max?: number;
}[] = [
  { pole: "rezia_pct", label: "Réžia (%)", desatinne: 2, max: 100 },
  { pole: "praca_eur_per_mj", label: "Práca (€/MJ)", desatinne: 4, max: 999999 },
  { pole: "nadmiera_pct", label: "Nadmiera (%)", desatinne: 2, max: 100 },
];

type MarzeVstup = Record<MarzovePole, string>;
const PRAZDNE_MARZE: MarzeVstup = { rezia_pct: "", praca_eur_per_mj: "", nadmiera_pct: "" };

/**
 * Zadané marže → telo zmeny alebo chyby po poliach. Prázdne pole sa
 * neposiela (ostane, ako je); nadmiera len v Materiáloch.
 */
export function marzeZoVstupu(
  vstup: MarzeVstup,
  sNadmierou: boolean,
): { zmena: Partial<Record<MarzovePole, string>>; chyby: Partial<Record<MarzovePole, string>> } {
  const zmena: Partial<Record<MarzovePole, string>> = {};
  const chyby: Partial<Record<MarzovePole, string>> = {};
  for (const { pole, desatinne, max } of MARZOVE_POLIA) {
    if (pole === "nadmiera_pct" && !sNadmierou) continue;
    const text = vstup[pole].trim();
    if (!text) continue;
    const vysledok = overCislo(text, { desatinne, max });
    if ("chyba" in vysledok) chyby[pole] = vysledok.chyba;
    else zmena[pole] = vysledok.hodnota;
  }
  return { zmena, chyby };
}

/**
 * Kategórie celého cenníka zoskupené podľa sekcie, záložka prvá —
 * zaradením sa položka môže presunúť (dĺžková krytka do Kovania).
 */
function skupinyKategorii(
  polozky: readonly KategoriaItem[],
  tab: MaterialovyTab,
): { sekcia: Sekcia; kody: string[] }[] {
  const podlaSekcie = new Map<Sekcia, Set<string>>();
  for (const p of polozky) {
    const kody = podlaSekcie.get(p.sekcia) ?? new Set<string>();
    kody.add(p.kod);
    podlaSekcie.set(p.sekcia, kody);
  }
  const poradie: Sekcia[] = [
    tab,
    ...(["materialy", "hrany", "kovanie"] as const).filter((s) => s !== tab),
  ];
  return poradie
    .filter((s) => podlaSekcie.has(s))
    .map((sekcia) => ({
      sekcia,
      kody: [...(podlaSekcie.get(sekcia) ?? [])].sort((a, b) =>
        formatKategoria(a).localeCompare(formatKategoria(b), "sk", { numeric: true }),
      ),
    }));
}

/**
 * Okno hromadnej úpravy Cenníka (KOLO 166) — jedna zmena pre všetky
 * vybrané položky. Počet je na tlačidle, aby user potvrdzoval číslo,
 * nie „OK".
 */
export function HromadnaUpravaDialog({
  akcia,
  pocet,
  tab,
  onClose,
  onPotvrdit,
}: Props): JSX.Element {
  return (
    <Dialog open={akcia !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {/* Telo s kľúčom akcie — každé otvorenie začína s prázdnym formulárom */}
        {akcia ? (
          <Telo
            key={akcia}
            akcia={akcia}
            pocet={pocet}
            tab={tab}
            onClose={onClose}
            onPotvrdit={onPotvrdit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Telo({
  akcia,
  pocet,
  tab,
  onClose,
  onPotvrdit,
}: Props & { akcia: HromadnaAkcia }): JSX.Element {
  const [kategoria, setKategoria] = useState("");
  const [novaKategoria, setNovaKategoria] = useState("");
  const [vyrobca, setVyrobca] = useState("");
  const [vyrobcaZmazat, setVyrobcaZmazat] = useState(false);
  // Preradenie väčšinou znamená „ber ceny tej kategórie" — preto zapnuté
  const [prevziatMarze, setPrevziatMarze] = useState(true);
  const [marze, setMarze] = useState<MarzeVstup>(PRAZDNE_MARZE);
  const [chyba, setChyba] = useState<string | null>(null);
  const [odosiela, setOdosiela] = useState(false);

  const kategorieQuery = useKategorie(undefined, { enabled: akcia === "kategoria" });
  const vyrobcoviaQuery = useVyrobcovia({ sekcia: tab }, { enabled: akcia === "vyrobca" });
  const skupiny = useMemo(
    () => skupinyKategorii(kategorieQuery.data?.data.polozky ?? [], tab),
    [kategorieQuery.data, tab],
  );

  const hrany = tab === "hrany";
  // Odpad (nadmieru) majú len dosky a dĺžkové materiály
  const sNadmierou = tab === "materialy";
  const zadaneMarze = marzeZoVstupu(marze, sNadmierou);
  const nadpis =
    akcia === "vyrobca" && hrany ? "Nastaviť kolekciu" : NADPIS[akcia];
  const polozky = `${formatPocet(pocet)} ${sklonuj(pocet, POLOZIEK)}`;
  const tlacidlo =
    akcia === "vyradit"
      ? `Vyradiť ${polozky}`
      : akcia === "obnovit"
        ? `Obnoviť ${polozky}`
        : `Zmeniť ${polozky}`;

  const cielovaKategoria = (
    kategoria === NOVA ? novaKategoria : kategoria
  ).trim();

  function zmena(): Partial<HromadnaZmena> | null {
    switch (akcia) {
      case "kategoria":
        if (!cielovaKategoria) return null;
        return prevziatMarze
          ? { kategoria: cielovaKategoria, prevziat_marze: true }
          : { kategoria: cielovaKategoria };
      case "marze":
        if (Object.keys(zadaneMarze.chyby).length > 0) return null;
        return Object.keys(zadaneMarze.zmena).length > 0 ? zadaneMarze.zmena : null;
      case "marze_kategorie":
        return { marze_podla_kategorie: true };
      case "vyrobca":
        if (vyrobcaZmazat) return { vyrobca_zmazat: true };
        return vyrobca.trim() ? { vyrobca: vyrobca.trim() } : null;
      case "vyradit":
        return { je_aktivny: false };
      case "obnovit":
        return { je_aktivny: true };
    }
  }

  const pripravena = zmena();

  async function potvrd(): Promise<void> {
    if (!pripravena) return;
    setChyba(null);
    setOdosiela(true);
    try {
      await onPotvrdit(pripravena);
    } catch (err) {
      setChyba(extractErrorMessage(err));
    } finally {
      setOdosiela(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{nadpis}</DialogTitle>
        <DialogDescription>
          Vybrané: {formatPocet(pocet)} · záložka {TAB_LABELS[tab]}
        </DialogDescription>
      </DialogHeader>

      {akcia === "kategoria" ? (
        <div className="space-y-1.5">
          <Label htmlFor="hromadne-kategoria">Nová kategória</Label>
          <Select value={kategoria} onValueChange={setKategoria}>
            <SelectTrigger id="hromadne-kategoria">
              <SelectValue placeholder="Vyber kategóriu" />
            </SelectTrigger>
            <SelectContent>
              {skupiny.map((g) => (
                <SelectGroup key={g.sekcia}>
                  <SelectLabel>{TAB_LABELS[g.sekcia as MaterialovyTab] ?? g.sekcia}</SelectLabel>
                  {g.kody.map((k) => (
                    <SelectItem key={`${g.sekcia}-${k}`} value={k}>
                      {formatKategoria(k)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              <SelectItem value={NOVA}>+ Nová kategória…</SelectItem>
            </SelectContent>
          </Select>
          {kategoria === NOVA ? (
            <Input
              aria-label="Kód novej kategórie"
              placeholder="napr. KOVANIE_KRYTKA"
              value={novaKategoria}
              onChange={(e) => setNovaKategoria(e.target.value)}
              maxLength={50}
              className="mt-1"
            />
          ) : null}
          <label className="flex items-start gap-2 pt-1 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={prevziatMarze}
              onChange={(e) => setPrevziatMarze(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              Dať im aj marže novej kategórie (réžia, práca, nadmiera z
              Nastavení cien). Položky s vlastnými maržami ostanú.
            </span>
          </label>
          <p className="text-[11px] text-muted-foreground">
            Zaradenie si appka zapamätá — re-import cenníka ho neprepíše.
            {prevziatMarze
              ? null
              : " Marže sa zatiaľ nezmenia; ďalší import cenníka dá položkám bez vlastných marží čísla novej kategórie."}
          </p>
        </div>
      ) : null}

      {akcia === "marze" ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {MARZOVE_POLIA.filter((p) => p.pole !== "nadmiera_pct" || sNadmierou).map(
              ({ pole, label }) => (
                <div key={pole} className="space-y-1">
                  <Label htmlFor={`hromadne-${pole}`} className="text-xs">
                    {label}
                  </Label>
                  <Input
                    id={`hromadne-${pole}`}
                    inputMode="decimal"
                    placeholder="bez zmeny"
                    value={marze[pole]}
                    onChange={(e) => setMarze({ ...marze, [pole]: e.target.value })}
                    aria-invalid={zadaneMarze.chyby[pole] ? true : undefined}
                    aria-describedby={
                      zadaneMarze.chyby[pole] ? `hromadne-${pole}-chyba` : undefined
                    }
                  />
                  {zadaneMarze.chyby[pole] ? (
                    <p id={`hromadne-${pole}-chyba`} className="text-[11px] text-destructive">
                      {zadaneMarze.chyby[pole]}
                    </p>
                  ) : null}
                </div>
              ),
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Prázdne pole ostane, ako je. Práca je v jednotke položky (m², m,
            ks).{sNadmierou ? " Nadmiera platí aj dekorom so smerom vlákien." : ""}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Iná hodnota než má kategória urobí z položky vlastnú maržu —
            import cenníka ani Nastavenia cien ju potom neprepíšu. Späť:{" "}
            <strong>Marže podľa kategórie</strong>.
          </p>
        </div>
      ) : null}

      {akcia === "marze_kategorie" ? (
        <p className="text-sm">
          Réžia, práca a nadmiera sa nastavia podľa Nastavení cien kategórie
          každej položky — aj položkám s vlastnými maržami. Odteraz ich zase
          riadi import cenníka a Nastavenia cien.
        </p>
      ) : null}

      {akcia === "vyrobca" ? (
        <div className="space-y-1.5">
          <Label htmlFor="hromadne-vyrobca">{hrany ? "Kolekcia" : "Výrobca"}</Label>
          <Input
            id="hromadne-vyrobca"
            list="hromadne-vyrobcovia"
            value={vyrobca}
            onChange={(e) => setVyrobca(e.target.value)}
            disabled={vyrobcaZmazat}
            maxLength={100}
            placeholder={hrany ? "napr. Egger" : "napr. Blum"}
          />
          <datalist id="hromadne-vyrobcovia">
            {(vyrobcoviaQuery.data?.data ?? []).map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={vyrobcaZmazat}
              onChange={(e) => setVyrobcaZmazat(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            {hrany ? "Vymazať kolekciu" : "Vymazať výrobcu"}
          </label>
        </div>
      ) : null}

      {akcia === "vyradit" ? (
        <p className="text-sm">
          Vybrané položky zmiznú z cenníka aj z výberov. V uložených
          zákazkách ostanú; vrátiť sa dajú v pohľade{" "}
          <strong>Len vyradené</strong>.
        </p>
      ) : null}

      {akcia === "obnovit" ? (
        <p className="text-sm">
          Vybrané položky sa vrátia do cenníka aj do výberov.
        </p>
      ) : null}

      {chyba ? (
        <p role="alert" className="text-sm text-destructive">
          {chyba}
        </p>
      ) : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Zrušiť
        </Button>
        <Button
          type="button"
          variant={akcia === "vyradit" ? "destructive" : "default"}
          onClick={() => void potvrd()}
          disabled={!pripravena || odosiela}
        >
          {odosiela ? "Ukladá sa…" : tlacidlo}
        </Button>
      </DialogFooter>
    </>
  );
}
